/**
 * SPEC-OverrideNotification §5.1 / §9.2 — global in-app notification handler.
 *
 * Mounted once at the authenticated app root (alongside EarlyRiskNotifier).
 * Responsibilities:
 *   1. Fetch unread notifications on mount via GET /api/qa/notifications
 *      (covers offline Agents per NOTIF-WEBAPP-3).
 *   2. Listen for live `notification` WS events (real-time path, NOTIF-WEBAPP-2).
 *   3. Render a toast per notification — specific copy for 'override', generic
 *      banner for unknown types (NOTIF-EXTENSION-UNKNOWN-TYPE extensibility
 *      contract, §7).
 *   4. Mark notifications as read via POST /api/qa/notifications/:id/read when
 *      the Agent dismisses or clicks the toast.
 *
 * Returns null — renders no DOM, only toasts.
 */

import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useRole } from "@/hooks/use-role";
import { useToast } from "@/hooks/use-toast";
import { useQaWebSocket, type QaNotificationEvent } from "@/hooks/use-websocket";

// ─── Pure copy builder (mirrors server-side composeOverrideMessage) ──────────

/**
 * Build the toast title and description for a notification.
 * Unknown notificationType values are handled gracefully (§7 extensibility
 * contract) — no crash, a generic message is returned.
 */
function renderNotificationCopy(
  notificationType: string,
  payload: Record<string, unknown>,
): { title: string; description: string } {
  switch (notificationType) {
    case "override": {
      const from = String(payload.fromDecision ?? "your decision");
      const to = String(payload.toDecision ?? "a new decision");
      const senior = String(payload.seniorName ?? "A reviewer");
      return {
        title: "🔄 Decision overridden",
        description: `${senior} changed this delivery's decision from ${from} to ${to}.`,
      };
    }
    default:
      // §7: forward-compatible fallback for future notification types.
      return {
        title: "📬 New notification",
        description: "You have a new notification for this delivery.",
      };
  }
}

// ─── Mark-read helper ────────────────────────────────────────────────────────

async function markNotificationRead(id: string): Promise<void> {
  try {
    await fetch(`/api/qa/notifications/${id}/read`, { method: "POST" });
  } catch (err) {
    console.error("[override-notification-handler] mark-read failed:", err);
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export function OverrideNotificationHandler() {
  const { user } = useAuth();
  const { role } = useRole();
  // SPEC-TrainingSimulator §8.5 — a trainee never opens a socket or reads
  // live notifications (App.tsx also unmounts this for trainees).
  const isTrainee = role === "trainee";
  const { toast } = useToast();
  const [, navigate] = useLocation();
  // Track already-shown notification IDs so we don't double-toast on
  // WS reconnect when a persisted row is also fetched via REST.
  const shown = useRef<Set<string>>(new Set());

  // Renders a toast for a single notification and marks it read.
  const handleNotification = useCallback(
    (id: string, notificationType: string, deliveryId: string | null, payload: Record<string, unknown>) => {
      if (shown.current.has(id)) return;
      shown.current.add(id);

      const { title, description } = renderNotificationCopy(notificationType, payload);

      toast({
        title,
        description,
        // Clicking the toast navigates to the affected delivery and marks read.
        onClick: () => {
          void markNotificationRead(id);
          if (deliveryId) navigate(`/qa/delivery/${deliveryId}`);
        },
        // Dismissal also marks read so the GET endpoint won't return it again.
        onOpenChange: (open: boolean) => {
          if (!open) void markNotificationRead(id);
        },
      } as any);
    },
    [toast, navigate],
  );

  // Fetch unread notifications on mount (offline-Agent path, NOTIF-WEBAPP-3).
  useEffect(() => {
    if (!user?.id || isTrainee) return;
    (async () => {
      try {
        const res = await fetch("/api/qa/notifications");
        if (!res.ok) return;
        const data: { notifications: Array<{ id: string; notificationType: string; deliveryId: string | null; payload: Record<string, unknown>; createdAt: string }> } = await res.json();
        for (const n of data.notifications ?? []) {
          handleNotification(n.id, n.notificationType, n.deliveryId, n.payload);
        }
      } catch (err) {
        console.error("[override-notification-handler] fetch failed:", err);
      }
    })();
  }, [user?.id, isTrainee, handleNotification]);

  // Live-update path: listen for `notification` WS events (NOTIF-WEBAPP-2).
  const handleWsNotification = useCallback(
    (e: QaNotificationEvent) => {
      handleNotification(e.notificationId, e.notificationType, e.deliveryId ?? null, e.payload);
    },
    [handleNotification],
  );

  useQaWebSocket(
    isTrainee ? null : (user?.id ?? null),
    // This component doesn't care about qa:update events — no-op callback.
    () => {},
    { userName: (user as any)?.firstName || undefined, email: user?.email || undefined },
    undefined, // claimHandlers — not needed here
    handleWsNotification,
  );

  return null;
}
